import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, Loader2, ShieldCheck, Search } from 'lucide-react';
import { LegalLayout } from '../components/LegalLayout';
import { getApiBaseUrl } from '../../../lib/config';
import '../legal.css';

/**
 * Public DSAR portal (Batch 2 PR #5).
 *
 * Anonymous landing page where any data subject (account-holder
 * or not) can submit a GDPR Art. 12–22 / CPRA / LGPD / PDPA
 * request: access, rectification, erasure, restriction,
 * portability or objection. The lower section is a status-lookup
 * form for tracking an existing ticket.
 *
 * UX notes:
 *   - The honeypot field (`websiteHoneypot`) is rendered with
 *     `display:none` + `tabIndex={-1}` + a non-screenreader label
 *     so real humans never see it.
 *   - The success state replaces the form with a confirmation
 *     card showing the ticket reference + the 30-day SLA + a
 *     deep-link back into the status-lookup form. The reference
 *     is the only handle the requester needs; an email
 *     acknowledgement is fired by the API.
 *   - The status-lookup form pastes the UUID, queries
 *     `GET /api/public/dsar/:ref`, and renders the public-shape
 *     row (no internal notes, no assignee).
 */

type RequestType = 'access' | 'rectification' | 'erasure' | 'restriction' | 'portability' | 'objection' | 'other';

const REQUEST_TYPE_LABELS: Record<RequestType, string> = {
  access: 'Access — send me a copy of my data (Art. 15)',
  rectification: 'Rectification — correct inaccurate data (Art. 16)',
  erasure: 'Erasure — delete my data (Art. 17 / "right to be forgotten")',
  restriction: 'Restriction — limit processing (Art. 18)',
  portability: 'Portability — export in machine-readable form (Art. 20)',
  objection: 'Objection — object to specific processing (Art. 21)',
  other: 'Other — describe in the message below',
};

interface SubmissionSuccess {
  ticketReference: string;
  dueAt: string;
  status: string;
}

interface PublicDsarRow {
  ticketReference: string;
  requestType: RequestType;
  status: string;
  submittedAt: string;
  dueAt: string;
  extendedAt: string | null;
  completedAt: string | null;
  publicResponse: {
    downloadUrl?: string;
    rejectionReason?: string;
    notes?: string;
  } | null;
}

export function DsarRequestPage() {
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<SubmissionSuccess | null>(null);

  const [submitterEmail, setSubmitterEmail] = useState('');
  const [submitterName, setSubmitterName] = useState('');
  const [requestType, setRequestType] = useState<RequestType>('access');
  const [orgSlug, setOrgSlug] = useState('');
  const [description, setDescription] = useState('');
  const [honeypot, setHoneypot] = useState('');

  // Status lookup state.
  const [lookupRef, setLookupRef] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [lookupRow, setLookupRow] = useState<PublicDsarRow | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`${getApiBaseUrl()}/api/public/dsar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          submitterEmail: submitterEmail.trim(),
          submitterName: submitterName.trim() || null,
          requestType,
          description: description.trim(),
          orgSlug: orgSlug.trim() || null,
          websiteHoneypot: honeypot || null,
        }),
      });
      const body = (await res.json()) as Partial<SubmissionSuccess & { error: string; code: string }>;
      if (!res.ok) {
        if (res.status === 429) {
          setSubmitError(
            "You've submitted several data requests recently. Please wait a few minutes before trying again.",
          );
        } else if (body.error) {
          setSubmitError(body.error);
        } else {
          setSubmitError('Could not submit your request. Please try again or email us at support@rynora.app.');
        }
        return;
      }
      if (body.ticketReference && body.dueAt && body.status) {
        setSubmitted({ ticketReference: body.ticketReference, dueAt: body.dueAt, status: body.status });
      } else {
        setSubmitError('Unexpected response from the server. Please try again.');
      }
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Could not reach the API. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const onLookup = async (e: FormEvent) => {
    e.preventDefault();
    setLookupError(null);
    setLookupRow(null);
    setLookupLoading(true);
    try {
      const ref = lookupRef.trim().toLowerCase();
      const res = await fetch(`${getApiBaseUrl()}/api/public/dsar/${encodeURIComponent(ref)}`);
      if (res.status === 404) {
        setLookupError(
          'We could not find a request with that ticket reference. Double-check it against your confirmation email.',
        );
        return;
      }
      if (!res.ok) {
        setLookupError('Could not load the request status. Please try again later.');
        return;
      }
      const body = (await res.json()) as PublicDsarRow;
      setLookupRow(body);
    } catch (err) {
      setLookupError(err instanceof Error ? err.message : 'Could not reach the API.');
    } finally {
      setLookupLoading(false);
    }
  };

  return (
    <LegalLayout
      title="Submit a data request"
      updated="June 1, 2026"
      summary="Anyone whose personal data Rynora holds can request access, rectification, deletion, portability, restriction, or objection. We respond within 30 days as required by GDPR Art. 12(3)."
      needsReview={false}
    >
      {!submitted ? (
        <>
          <p>
            Under the EU&nbsp;GDPR, UK&nbsp;GDPR, California&nbsp;CPRA, Brazil&nbsp;LGPD, India&nbsp;DPDP and
            Singapore&nbsp;PDPA you have the right to ask us about the personal data we hold about you and to instruct
            us to act on it. Use this form to file a request. You do <strong>not</strong> need a Rynora account — anyone
            whose data we may hold (including data subjects whose information was uploaded by a customer using our
            service) is entitled to submit.
          </p>

          <h2>1. About your request</h2>
          <form className="dsar-form" onSubmit={onSubmit} noValidate>
            <label className="dsar-form__field">
              <span className="dsar-form__label">Email address (required)</span>
              <input
                type="email"
                required
                value={submitterEmail}
                onChange={(e) => setSubmitterEmail(e.target.value)}
                placeholder="you@example.com"
                aria-describedby="dsar-email-hint"
              />
              <span id="dsar-email-hint" className="dsar-form__hint">
                We email a ticket reference here and use it to verify your identity before fulfilling the request.
              </span>
            </label>

            <label className="dsar-form__field">
              <span className="dsar-form__label">Your name (optional)</span>
              <input
                type="text"
                value={submitterName}
                onChange={(e) => setSubmitterName(e.target.value)}
                placeholder="First Last"
              />
              <span className="dsar-form__hint">
                Optional. We do not require a name to honour your request (data-minimisation principle).
              </span>
            </label>

            <label className="dsar-form__field">
              <span className="dsar-form__label">Type of request (required)</span>
              <select value={requestType} onChange={(e) => setRequestType(e.target.value as RequestType)}>
                {(Object.keys(REQUEST_TYPE_LABELS) as RequestType[]).map((k) => (
                  <option key={k} value={k}>
                    {REQUEST_TYPE_LABELS[k]}
                  </option>
                ))}
              </select>
            </label>

            <label className="dsar-form__field">
              <span className="dsar-form__label">Organisation slug (optional)</span>
              <input
                type="text"
                value={orgSlug}
                onChange={(e) => setOrgSlug(e.target.value)}
                placeholder="acme-corp"
                aria-describedby="dsar-orgslug-hint"
              />
              <span id="dsar-orgslug-hint" className="dsar-form__hint">
                If you know which Rynora workspace holds your data, paste its slug (from the URL). Leaving this blank is
                fine — we will route the request manually if needed.
              </span>
            </label>

            <label className="dsar-form__field">
              <span className="dsar-form__label">Message (required, 10–5000 characters)</span>
              <textarea
                required
                rows={6}
                minLength={10}
                maxLength={5000}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe what you'd like us to do, and (if you know it) any identifiers we may have on file: account email, organisation name, run id, etc."
              />
              <span className="dsar-form__hint">{description.length} / 5000 characters.</span>
            </label>

            {/* Honeypot: hidden from real users; bots tend to fill every visible
                input. The server returns a synthesised 202 + dropped row on
                non-empty values, so the bot believes the submission worked. */}
            <label
              aria-hidden="true"
              tabIndex={-1}
              style={{ position: 'absolute', left: '-9999px', top: 'auto', width: 1, height: 1, overflow: 'hidden' }}
            >
              Website
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
              />
            </label>

            {submitError && (
              <div className="dsar-form__error" role="alert">
                <AlertTriangle size={16} aria-hidden="true" />
                <span>{submitError}</span>
              </div>
            )}

            <button type="submit" className="dsar-form__submit" disabled={submitting || description.length < 10}>
              {submitting ? (
                <>
                  <Loader2 size={16} className="dsar-form__spinner" aria-hidden="true" /> Submitting…
                </>
              ) : (
                <>
                  <ShieldCheck size={16} aria-hidden="true" /> Submit request
                </>
              )}
            </button>
          </form>
        </>
      ) : (
        <div className="dsar-confirm" role="status" aria-live="polite">
          <div className="dsar-confirm__head">
            <CheckCircle2 size={24} aria-hidden="true" />
            <div>
              <h2>We received your request.</h2>
              <p>
                We will respond by <strong>{new Date(submitted.dueAt).toLocaleDateString()}</strong> at the latest (GDPR
                Art. 12(3) gives us up to 30 days; we usually answer well before that).
              </p>
            </div>
          </div>
          <dl className="dsar-confirm__grid">
            <dt>Ticket reference</dt>
            <dd>
              <code>{submitted.ticketReference}</code>
            </dd>
            <dt>Status</dt>
            <dd>
              <code>{submitted.status}</code>
            </dd>
            <dt>Confirmation email</dt>
            <dd>
              Sent to the address on the form. Save the ticket reference somewhere safe — the status form below uses it.
            </dd>
          </dl>
          <button
            type="button"
            className="dsar-form__submit"
            onClick={() => {
              setSubmitted(null);
              setLookupRef(submitted.ticketReference);
              setSubmitterEmail('');
              setSubmitterName('');
              setDescription('');
              setOrgSlug('');
            }}
          >
            File another request
          </button>
        </div>
      )}

      <h2>2. Check the status of an existing request</h2>
      <p>Paste the ticket reference from your confirmation email to see the current status.</p>
      <form className="dsar-form dsar-form--lookup" onSubmit={onLookup} noValidate>
        <label className="dsar-form__field">
          <span className="dsar-form__label">Ticket reference</span>
          <input
            type="text"
            value={lookupRef}
            onChange={(e) => setLookupRef(e.target.value)}
            placeholder="00000000-0000-0000-0000-000000000000"
            spellCheck={false}
            autoCapitalize="none"
            autoCorrect="off"
            inputMode="text"
          />
        </label>
        {lookupError && (
          <div className="dsar-form__error" role="alert">
            <AlertTriangle size={16} aria-hidden="true" />
            <span>{lookupError}</span>
          </div>
        )}
        <button type="submit" className="dsar-form__submit" disabled={lookupLoading || lookupRef.trim().length === 0}>
          {lookupLoading ? (
            <>
              <Loader2 size={16} className="dsar-form__spinner" aria-hidden="true" /> Looking up…
            </>
          ) : (
            <>
              <Search size={16} aria-hidden="true" /> Check status
            </>
          )}
        </button>
      </form>

      {lookupRow && (
        <div className="dsar-status" role="status" aria-live="polite">
          <h3>Status for {lookupRow.ticketReference}</h3>
          <dl className="dsar-confirm__grid">
            <dt>Type</dt>
            <dd>{REQUEST_TYPE_LABELS[lookupRow.requestType]}</dd>
            <dt>Status</dt>
            <dd>
              <code>{lookupRow.status}</code>
            </dd>
            <dt>Submitted</dt>
            <dd>{new Date(lookupRow.submittedAt).toLocaleString()}</dd>
            <dt>Due by</dt>
            <dd>
              {new Date(lookupRow.dueAt).toLocaleDateString()}
              {lookupRow.extendedAt && (
                <span className="dsar-status__extended">
                  {' '}
                  (extended on {new Date(lookupRow.extendedAt).toLocaleDateString()})
                </span>
              )}
            </dd>
            {lookupRow.completedAt && (
              <>
                <dt>Completed</dt>
                <dd>{new Date(lookupRow.completedAt).toLocaleString()}</dd>
              </>
            )}
            {lookupRow.publicResponse?.downloadUrl && (
              <>
                <dt>Download</dt>
                <dd>
                  <a href={lookupRow.publicResponse.downloadUrl} target="_blank" rel="noreferrer noopener">
                    Open your data export
                  </a>
                </dd>
              </>
            )}
            {lookupRow.publicResponse?.notes && (
              <>
                <dt>Notes</dt>
                <dd>{lookupRow.publicResponse.notes}</dd>
              </>
            )}
            {lookupRow.publicResponse?.rejectionReason && (
              <>
                <dt>Reason for rejection</dt>
                <dd>{lookupRow.publicResponse.rejectionReason}</dd>
              </>
            )}
          </dl>
        </div>
      )}

      <h2>3. How we handle your request</h2>
      <ol>
        <li>We acknowledge receipt of your request via email and assign it a ticket reference (above).</li>
        <li>
          We verify your identity using the email you submitted from. We may ask for additional identifiers if there is
          reasonable doubt.
        </li>
        <li>
          We route the request to the workspace owner whose data is involved. They have access to your data and are
          responsible for fulfilling the request. Their actions are mirrored to our audit log.
        </li>
        <li>
          We notify you when the request is fulfilled, rejected (with reason), or extended. The maximum response window
          is 30 days; extensions of up to a further 60 days are permitted under Art. 12(3) for complex requests, with
          reasons provided.
        </li>
      </ol>

      <h2>4. Who to contact</h2>
      <p>
        Any other privacy concerns? Email <a href="mailto:privacy@rynora.app">privacy@rynora.app</a> directly. For
        general questions about how we handle data, see our <Link to="/privacy">Privacy Policy</Link> and the{' '}
        <Link to="/trust">Trust Center</Link>.
      </p>
    </LegalLayout>
  );
}
